import React from 'react';
import { CogIcon } from '@heroicons/react/24/outline';
import { HChapter, HMetaphor, HSection, HParagraph, HTip, HBold } from '../HandbookContent';

export default function ChapterActions() {
  return (
    <HChapter
      title="Actions: What Happens Automatically"
      subtitle="Actions are the automated tasks that fire after a successful transition."
    >
      <HMetaphor
        icon={<CogIcon className="w-5 h-5 text-lex-accent" />}
        title="Think of Actions as the Autopilot"
      >
        Once you've passed the tollbooth (guard) and the checkpoint (validator) and made it onto the
        new stretch of highway, the autopilot takes over. It sends a notification to the client, updates
        the calendar, calculates the next deadline — all without you lifting a finger. Actions are the
        work that the system does for you after each successful move.
      </HMetaphor>

      <HSection title="What Actions Can Do">
        <HParagraph>
          An <HBold>action</HBold> is a task that runs automatically after a transition completes
          successfully. Actions happen in a defined order, and each one can perform a specific job:
        </HParagraph>
        <ul className="list-disc pl-6 text-sm text-gray-700 dark:text-gray-300 leading-relaxed mb-4 space-y-2">
          <li>
            <HBold>Send a notification</HBold> — Email the client, alert the team, or notify a supervisor
            that something has changed. Example: "Client notified that discovery documents are ready for review."
          </li>
          <li>
            <HBold>Calculate a deadline</HBold> — Automatically compute the next statutory deadline based
            on court rules. Example: "Response deadline set to 30 days from service date."
          </li>
          <li>
            <HBold>Fire a webhook</HBold> — Send data to an external system (billing software, case
            management, court filing). Example: "Notify billing system that case moved to trial preparation."
          </li>
          <li>
            <HBold>Update a field</HBold> — Automatically set a value on the matter or task. Example:
            "Set the case status date to today's date."
          </li>
          <li>
            <HBold>Create a task</HBold> — Spawn a new task or sub-task when a transition occurs.
            Example: "Create a 'Draft Motion' task when case enters the Motion phase."
          </li>
        </ul>
      </HSection>

      <HSection title="Execution Order">
        <HParagraph>
          Actions run in a specific order, set by you when you configure them. If you have three
          actions on a transition, they'll execute as action 1, then 2, then 3. The order matters
          when one action depends on another — for example, you might calculate a deadline first,
          then send a notification that includes that deadline.
        </HParagraph>
      </HSection>

      <HSection title="Error Handling">
        <HParagraph>
          Sometimes an action fails — maybe the email server is down, or the webhook endpoint is
          unreachable. EdgePlaybook handles this in two modes:
        </HParagraph>
        <ul className="list-disc pl-6 text-sm text-gray-700 dark:text-gray-300 leading-relaxed mb-4 space-y-2">
          <li>
            <HBold>Continue on error</HBold> — The transition succeeds even if the action fails.
            The failure is logged but doesn't block progress. Use this for non-critical actions
            like notifications.
          </li>
          <li>
            <HBold>Rollback on error</HBold> — If the action fails, the entire transition is
            rolled back. The matter stays at its previous status. Use this for critical actions
            like deadline calculations.
          </li>
        </ul>
      </HSection>

      <HTip>
        When linking a court rule deadline to a transition, use the Court Rules browser in the Visual
        Editor. It auto-creates a "calculate_deadline" action with the correct statutory parameters.
      </HTip>
    </HChapter>
  );
}
